import {
  ArrowDownLeft,
  ArrowUpRight,
  Check, CheckCircle, Clock, Download, Edit, ExternalLink, Eye, Mail, Pause, Tag, User, Users, X,
} from 'react-feather';
import { DocumentNode } from 'graphql';
import { Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetRegistrationSummaryQuery as GetRegistrationSummary,
  LotteryStatus,
  ParticipantAttribute,
  Permission,
  WaitlistInviteStatus,
} from '__generated__/graphql';

import { ContainerWithOptions, ExternalOption, LinkOption, Option } from '../../../../common/Common/OptionsDropdown';
import { TicketLabel } from '../../../../common/Common/SellableLabel';
import { encodeUrl, replaceUrlParams } from '../../../../common/helpers';
import { useSession } from '../../../Auth/SessionProvider';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import ParticipantFieldValue from '../../../../common/ParticipantFields/ParticipantFieldValue';
import RegistrationAttribute from '../../../../common/Registrations/RegistrationAttribute';
import RegistrationStatusLabel from '../../../Common/RegistrationStatusLabel';
import UI from '../../../../common/UI';
import UpgradeDetails from './UpgradeDetails';
import useEditDrawResultModal from './useEditDrawResultModal';
import useEditPurchaseModal from '../Orders/useEditPurchaseModal';
import useEditRegistrationModal from './useEditRegistrationModal';
import useEditTeamModal from './useEditTeamModal';
import useEditTicketNumberModal from './useEditTicketNumberModal';
import useLocale from '../../../../common/useLocale';
import useProject from '../../../useProject';
import useScroll from '../../../../common/useScroll';
import useSendConfirmationModal from './useSendConfirmationModal';
import useSetStatusModal from './useSetStatusModal';
import useTimeSlotFormatter from '../../../../common/Tickets/useTimeSlotFormatter';

type Registration = GetRegistrationSummary['registrationSummary']['registrations'][0];

export interface RegistrationDetailsProps {
  registration: Registration;
  eventId: string;
  participantId: string;
  highlight?: boolean;
  expanded?: boolean;
  refetchQueries?: DocumentNode[];
}

const RegistrationDetails = ({
  registration,
  eventId,
  participantId,
  highlight,
  expanded: initialExpanded,
  refetchQueries = [
    GetParticipantDataQuery,
    GetRegistrationSummaryQuery,
  ],
}: RegistrationDetailsProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const { scrollTo } = useScroll();
  const { hasPermission } = useSession();
  const project = useProject();
  const formatTimeSlot = useTimeSlotFormatter();

  const [openEditRegistrationModal] = useEditRegistrationModal({
    registrationId: registration.id,
    refetchQueries,
  });

  const [openSetTeamModel] = useEditTeamModal({
    registrationId: registration.id,
    refetchQueries,
  });

  const [openEditTicketNumberModal] = useEditTicketNumberModal({
    registration,
    refetchQueries,
  });

  const [openSendConfirmationModal] = useSendConfirmationModal({
    registration,
    refetchQueries,
  });

  const [openSetStatusModal] = useSetStatusModal({
    registrationId: registration.id,
    refetchQueries,
  });

  const [openEditPurchaseModal] = useEditPurchaseModal({
    purchaseId: registration.purchase.id,
    eventId,
    refetchQueries,
  });

  const [openEditDrawResultModal] = useEditDrawResultModal({
    registration,
    refetchQueries,
  });

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  // Attributes are shown when they are required or not empty.
  const registrationAttributes = [
    ParticipantAttribute.date_of_birth,
    ParticipantAttribute.gender,
    ParticipantAttribute.nationality,
    ParticipantAttribute.address,
    ParticipantAttribute.phone,
    ParticipantAttribute.emergency_phone,
  ].filter((attribute) => {
    if (registration.required_attributes.includes(attribute)) {
      return true;
    }

    if (attribute === ParticipantAttribute.address) {
      return !!registration.street;
    }

    if (attribute === ParticipantAttribute.date_of_birth) {
      return !!registration.date_of_birth;
    }

    if (attribute === ParticipantAttribute.gender) {
      return !!registration.gender;
    }

    if (attribute === ParticipantAttribute.nationality) {
      return !!registration.nationality;
    }

    if (attribute === ParticipantAttribute.phone) {
      return !!registration.phone;
    }

    if (attribute === ParticipantAttribute.emergency_phone) {
      return !!registration.emergency_phone;
    }

    return false;
  });

  /**
   * An array of UI sections containing information about the registration,
   * to make it easy to add separators between sections without having to
   * resort to very long if-statements.
   */
  const sections: ReactNode[][] = [[]];

  sections[sections.length - 1].push(
    <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
      <UI.Legend>
        {t('ticket')}
      </UI.Legend>
      <UI.Div>
        <UI.Icon>
          <UI.Icons.Ticket />
        </UI.Icon>
        {' '}
        {registration.ticket.title}
      </UI.Div>
      {hasEditPermission && (
        <UI.Div sc={{ textAlign: ['left', 'right'] }}>
          <UI.Button
            onClick={() => openEditPurchaseModal()}
            sc={{ blank: true, size: 'sm', my: -0.75 }}
            aria-label={t('edit_purchase')}
          >
            <UI.Icon>
              <Edit />
            </UI.Icon>
            {' '}
            {t('edit')}
          </UI.Button>
        </UI.Div>
      )}
    </UI.GridContainer>,
  );

  if (registration.ticket.has_time_slots) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('time_slot')}
        </UI.Legend>
        <UI.Div sc={{ muted: !registration.time_slot }}>
          <UI.Icon>
            <Clock />
          </UI.Icon>
          {' '}
          {registration.time_slot ? (
            <UI.Delimit>
              {formatTimeSlot(registration.time_slot)}
              {registration.time_slot.title}
            </UI.Delimit>
          ) : t('no_time_slot')}
        </UI.Div>
      </UI.GridContainer>,
    );
  }

  // Start a new section.
  sections.push([]);

  if (registration.event.has_ticket_numbers) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('ticket_number')}
        </UI.Legend>
        <UI.Div sc={{ muted: !registration.ticket_number }}>
          <UI.Icon>
            <UI.Icons.StartFinish />
          </UI.Icon>
          {' '}
          {registration.ticket_number ? (
            <UI.Delimit>
              {registration.ticket_number}
              {registration.ticket_number_sequence?.corral_name}
              {registration.ticket_number_sequence?.start_time && formatDate(
                parseDate(
                  registration.ticket_number_sequence.start_time, { format: 'internal_time' },
                ),
                { format: 'display_time' },
              )}
            </UI.Delimit>
          ) : t('no_ticket_number')}
        </UI.Div>
        {hasEditPermission && (
          <UI.Div sc={{ textAlign: ['left', 'right'] }}>
            <UI.Button
              onClick={() => openEditTicketNumberModal()}
              sc={{ blank: true, size: 'sm', my: -0.75 }}
              aria-label={t('edit_ticket_number')}
            >
              <UI.Icon>
                <Edit />
              </UI.Icon>
              {' '}
              {t('edit')}
            </UI.Button>
          </UI.Div>
        )}
      </UI.GridContainer>,
    );
  }

  if (registration.team || registration.event.allow_teams) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('team')}
        </UI.Legend>

        <UI.Div>
          {registration.team ? (
            <UI.Link to={`/${project.id}/participants/teams/${registration.team.id}`}>
              <UI.Icon>
                <Users />
              </UI.Icon>
              {' '}
              {registration.team.title}
            </UI.Link>
          ) : (
            <UI.Span sc={{ muted: true }}>
              <UI.Icon>
                <Users />
              </UI.Icon>
              {' '}
              {t('no_team')}
            </UI.Span>
          )}
        </UI.Div>
        {hasEditPermission && (
          <UI.Div sc={{ textAlign: ['left', 'right'] }}>
            <UI.Button
              onClick={() => openSetTeamModel()}
              sc={{ blank: true, size: 'sm', my: -0.75 }}
              aria-label={t('edit_team_membership')}
            >
              <UI.Icon>
                <Edit />
              </UI.Icon>
              {' '}
              {t('edit')}
            </UI.Button>
          </UI.Div>
        )}
      </UI.GridContainer>,
    );
  }

  if (sections[sections.length - 1].length > 0) {
    // Start a new section.
    sections.push([]);
  }

  sections[sections.length - 1].push(
    <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
      <UI.Legend>
        {t('personal_details')}
      </UI.Legend>
      <UI.GridContainer sc={{ gutter: 0.5 }}>
        {registration.assigned && (
          <>
            <UI.Div>
              <UI.Legend sc={{ fontWeight: 500 }}>
                {t('common:participant_attributes.email_address')}
              </UI.Legend>
              {registration.participant.email}
            </UI.Div>
            {registrationAttributes.map((attribute) => (
              <RegistrationAttribute
                registration={registration}
                attribute={attribute}
                showEmptyWarning
                key={attribute}
              />
            ))}
            {registration.participant_fields.map((field) => (
              <ParticipantFieldValue
                fieldEntry={{
                  value: null,
                  choice_entries: [],
                  field: {
                    id: field.id,
                  },
                  ...registration.participant_field_entries.filter(
                    (fieldEntry) => fieldEntry.field.id === field.id,
                  )[0],
                }}
                field={field}
                key={field.id}
              />
            ))}
          </>
        )}
        {!registration.assigned && (
          <UI.Div sc={{ muted: true }}>
            {t('common:participant_attributes.not_entered')}
          </UI.Div>
        )}
      </UI.GridContainer>
      {hasEditPermission && (
        <UI.Div sc={{ textAlign: ['left', 'right'] }}>
          <UI.Button
            onClick={() => openEditRegistrationModal()}
            sc={{ blank: true, size: 'sm', my: -0.75 }}
            aria-label={t('edit_registration')}
          >
            <UI.Icon>
              <Edit />
            </UI.Icon>
            {' '}
            {t('edit')}
          </UI.Button>
        </UI.Div>
      )}
    </UI.GridContainer>,
  );

  if (sections[sections.length - 1].length > 0) {
    // Start a new section.
    sections.push([]);
  }

  if (registration.purchase.invitation_code) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('invitation_code')}
        </UI.Legend>

        <UI.Div>
          {registration.purchase.invitation_code.value}
        </UI.Div>

        {hasEditPermission && (
          <UI.Div sc={{ textAlign: ['left', 'right'] }}>
            <UI.Button
              onClick={() => openEditPurchaseModal()}
              sc={{ blank: true, size: 'sm', my: -0.75 }}
              aria-label={t('edit_invitation_code')}
            >
              <UI.Icon>
                <Edit />
              </UI.Icon>
              {' '}
              {t('edit')}
            </UI.Button>
          </UI.Div>
        )}
      </UI.GridContainer>,
    );
  }

  if (sections[sections.length - 1].length > 0) {
    // Start a new section.
    sections.push([]);
  }

  if (registration.charity) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('fundraising')}
        </UI.Legend>

        <UI.Div>
          {!registration.fundraising_campaign && registration.charity.title}
          {registration.fundraising_campaign && (
          <UI.A
            href={registration.fundraising_campaign.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {registration.charity.title}
            {' '}
            <UI.Icon>
              <ExternalLink />
            </UI.Icon>
          </UI.A>
          )}
        </UI.Div>
      </UI.GridContainer>,
    );
  }

  if (sections[sections.length - 1].length > 0) {
    // Start a new section.
    sections.push([]);
  }

  if (registration.scans.length > 0) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('scanners', { count: registration.scans.length })}
        </UI.Legend>
        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {registration.scans.map((scan) => (
            <UI.Div key={scan.id}>
              <UI.Legend sc={{ fontWeight: 500 }}>
                {scan.scanner.title}
              </UI.Legend>
              <UI.Span sc={{ muted: !scan.scanned_at }}>
                <UI.Icon>
                  {scan.scanned_at && <Check />}
                  {!scan.scanned_at && <X />}
                </UI.Icon>
                {' '}
                {scan.scanned_at && formatDate(
                  parseDate(scan.scanned_at, { timezone: 'UTC' }),
                  { format: 'display_date_time' },
                )}
                {!scan.scanned_at && t('not_checked_in')}
              </UI.Span>
            </UI.Div>
          ))}
        </UI.GridContainer>
      </UI.GridContainer>,
    );
  }

  if (sections[sections.length - 1].length > 0) {
    // Start a new section.
    sections.push([]);
  }

  if (participantId !== registration.participant.id) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('assigned_to')}
        </UI.Legend>

        <UI.Div>
          <UI.Link
            to={`/${project.id}/participants/${registration.participant.id}${replaceUrlParams({
              event: eventId,
              search: `ATL${registration.sequence}`,
            })}`}
          >
            <UI.Icon>
              <User />
            </UI.Icon>
            {' '}
            {registration.participant.full_name}
          </UI.Link>
        </UI.Div>
      </UI.GridContainer>,
    );
  }

  if (registration.purchase.order.participant.id !== participantId) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('purchased_by')}
        </UI.Legend>

        <UI.Div>
          <UI.Link to={`/${project.id}/participants/${registration.purchase.order.participant.id}${replaceUrlParams({ event: eventId })}`}>
            <UI.Icon>
              <User />
            </UI.Icon>
            {' '}
            {registration.purchase.order.participant.full_name}
          </UI.Link>
        </UI.Div>
      </UI.GridContainer>,
    );
  }

  if (registration.waitlist_invites.length > 0) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('waitlist_invitations')}
        </UI.Legend>

        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {registration.waitlist_invites.map((invite) => (
            <UI.Div key={invite.id}>
              <UI.Link
                to={`/${project.id}/participants/${invite.resale_registration.owner.id}${replaceUrlParams({
                  event: eventId,
                  search: `ATL${invite.resale_registration.sequence}`,
                })}`}
                sc={{ mr: 2 }}
              >
                <UI.Icon>
                  <ArrowDownLeft />
                </UI.Icon>
                {' '}
                {invite.resale_registration.owner.full_name}
              </UI.Link>
              <UI.Small sc={{ color: 'gray.500' }}>
                <UI.Delimit>
                  <UI.Span>
                    <UI.Icon>
                      <Mail />
                    </UI.Icon>
                    {' '}
                    {formatDate(
                      parseDate(invite.created_at, { timezone: 'UTC' }),
                      { format: 'display_compact_date_time' },
                    )}
                  </UI.Span>
                  {invite.status === WaitlistInviteStatus.accepted && t('accepted')}
                  {invite.status === WaitlistInviteStatus.expired && t('expired')}
                  {invite.status === WaitlistInviteStatus.pending && t('pending')}
                  {invite.status === WaitlistInviteStatus.rejected && t('rejected')}
                </UI.Delimit>
              </UI.Small>
            </UI.Div>
          ))}
          {registration.waitlist_paused && (
            <UI.Div>
              <UI.Small sc={{ color: 'gray.500' }}>
                <UI.Icon>
                  <Pause />
                </UI.Icon>
                {' '}
                {t('waitlist_position_paused')}
              </UI.Small>
            </UI.Div>
          )}
        </UI.GridContainer>
      </UI.GridContainer>,
    );
  }

  if (registration.resold_by
    && !registration.waitlist_invites.some((invite) => invite.resale_registration.id === registration.resold_by.id)) {
    // Resold, but not via waitlist
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('resold_by')}
        </UI.Legend>

        <UI.Div>
          <UI.Link
            to={`/${project.id}/participants/${registration.resold_by.owner.id}${replaceUrlParams({
              event: eventId,
              search: `ATL${registration.resold_by.sequence}`,
            })}`}
          >
            <UI.Icon>
              <ArrowDownLeft />
            </UI.Icon>
            {' '}
            {registration.resold_by.owner.full_name}
          </UI.Link>
        </UI.Div>
      </UI.GridContainer>,
    );
  }

  if (registration.waitlist_invitees.length > 0) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('offered_to_waitlist')}
        </UI.Legend>

        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {registration.waitlist_invitees.map((invite) => (
            <UI.Div key={invite.id}>
              <UI.Link
                to={`/${project.id}/participants/${invite.waitlist_registration.owner.id}${replaceUrlParams({
                  event: eventId,
                  search: `ATL${invite.waitlist_registration.sequence}`,
                })}`}
                sc={{ mr: 2 }}
              >
                <UI.Icon>
                  <ArrowUpRight />
                </UI.Icon>
                {' '}
                {invite.waitlist_registration.owner.full_name}
              </UI.Link>
              <UI.Small sc={{ color: 'gray.500' }}>
                <UI.Delimit>
                  <UI.Span>
                    <UI.Icon>
                      <Mail />
                    </UI.Icon>
                    {' '}
                    {formatDate(
                      parseDate(invite.created_at, { timezone: 'UTC' }),
                      { format: 'display_compact_date_time' },
                    )}
                  </UI.Span>
                  {invite.status === WaitlistInviteStatus.accepted && t('accepted')}
                  {invite.status === WaitlistInviteStatus.expired && t('expired')}
                  {invite.status === WaitlistInviteStatus.pending && t('pending')}
                  {invite.status === WaitlistInviteStatus.rejected && t('rejected')}
                </UI.Delimit>
              </UI.Small>
            </UI.Div>
          ))}
        </UI.GridContainer>
      </UI.GridContainer>,
    );
  }

  if (registration.resold_to
    && !registration.waitlist_invitees.some((invite) => (
      invite.waitlist_registration.id === registration.resold_to.id
    ))) {
    // Resold, but not via waitlist
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('resold_to')}
        </UI.Legend>

        <UI.Div>
          <UI.Link
            to={`/${project.id}/participants/${registration.resold_to.owner.id}${replaceUrlParams({
              event: eventId,
              search: `ATL${registration.resold_to.sequence}`,
            })}`}
          >
            <UI.Icon>
              <ArrowUpRight />
            </UI.Icon>
            {' '}
            {registration.resold_to.owner.full_name}
          </UI.Link>
        </UI.Div>
      </UI.GridContainer>,
    );
  }

  if (registration.preregistration && registration.event.has_lotteries) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('lottery')}
        </UI.Legend>

        <UI.GridContainer>
          <UI.Div>
            <UI.Legend sc={{ fontWeight: 500 }}>
              {t('selected_by_lottery')}
            </UI.Legend>
            <UI.Div sc={{ muted: !registration.lottery }}>
              <UI.Icon>
                {registration.lottery ? <Check /> : <X />}
              </UI.Icon>
              {' '}
              {registration.lottery && (
                <UI.Delimit>
                  {registration.lottery.title}
                  {registration.preregistration.lottery_status === LotteryStatus.selected && (
                    t('unconfirmed')
                  )}
                  {registration.preregistration.lottery_status === LotteryStatus.confirmed && (
                    t('confirmed')
                  )}
                  {registration.preregistration.lottery_status === LotteryStatus.expired && (
                    t('expired')
                  )}
                </UI.Delimit>
              )}
              {!registration.lottery && t('not_selected')}
            </UI.Div>
          </UI.Div>

          {registration.rejected_lotteries.length > 0 && (
            <UI.Div>
              <UI.Legend sc={{ fontWeight: 500 }}>
                {t('rejected_by_lotteries')}
              </UI.Legend>

              <UI.UL sc={{ unstyled: true }}>
                {registration.rejected_lotteries.map((rejectedLottery) => (
                  <UI.LI key={rejectedLottery.id}>
                    <UI.Icon>
                      <X />
                    </UI.Icon>
                    {' '}
                    {rejectedLottery.title}
                  </UI.LI>
                ))}
              </UI.UL>
            </UI.Div>
          )}
        </UI.GridContainer>
        {hasEditPermission && (
          <UI.Div sc={{ textAlign: ['left', 'right'] }}>
            <UI.Button
              onClick={() => openEditDrawResultModal()}
              sc={{ blank: true, size: 'sm', my: -0.75 }}
              aria-label={t('edit_draw_result')}
            >
              <UI.Icon>
                <Edit />
              </UI.Icon>
              {' '}
              {t('edit')}
            </UI.Button>
          </UI.Div>
        )}
      </UI.GridContainer>,
    );
  }

  if (registration.preregistered_for) {
    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('preregistration_for')}
        </UI.Legend>

        <UI.Div>
          <UI.Link
            to={`/${project.id}/participants/${registration.preregistered_for.participant.id}${replaceUrlParams({
              event: eventId,
              search: `ATL${registration.preregistered_for.sequence}`,
            })}`}
          >
            <UI.Icon>
              <User />
            </UI.Icon>
            {' '}
            <UI.Delimit>
              {registration.preregistered_for.full_name || t('not_assigned_yet')}
              {registration.preregistered_for.ticket.title}
            </UI.Delimit>
          </UI.Link>
        </UI.Div>
      </UI.GridContainer>,
    );
  }

  if (registration.preregistered_by) {
    if (registration.preregistered_by.lottery) {
      sections[sections.length - 1].push(
        <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
          <UI.Legend>
            {t('originating_from_lottery')}
          </UI.Legend>

          <UI.Div>
            {registration.preregistered_by.lottery.title}
          </UI.Div>
        </UI.GridContainer>,
      );
    }

    sections[sections.length - 1].push(
      <UI.GridContainer sc={{ columns: ['1fr', '200px 2fr 100px'], gutter: [0.25, 0.5] }}>
        <UI.Legend>
          {t('preregistration')}
        </UI.Legend>

        <UI.Div>
          <UI.Link
            to={`/${project.id}/participants/${registration.preregistered_by.participant.id}${replaceUrlParams({
              event: eventId,
              search: `ATL${registration.preregistered_by.sequence}`,
            })}`}
          >
            <UI.Icon>
              <User />
            </UI.Icon>
            {' '}
            <UI.Delimit>
              {registration.preregistered_by.full_name || t('not_assigned_yet')}
              {registration.preregistered_by.ticket.title}
            </UI.Delimit>
          </UI.Link>
        </UI.Div>
      </UI.GridContainer>,
    );
  }

  if (sections[sections.length - 1].length === 0) {
    // Remove the last section if it is empty.
    sections.pop();
  }

  const handleAnchorClick = () => {
    setExpanded(true);
    scrollTo(`RegistrationSummary_${registration.event.id}`, { gutter: 1 });
  };

  const [expanded, setExpanded] = useState(initialExpanded);

  return (
    <UI.CollapsibleNavCard
      onClick={() => setExpanded((expanded) => !expanded)}
      open={expanded}
      sc={{
        outline: expanded ? 'gray.250' : 'gray.200',
        highlight,
        borderRadius: 'lg',
      }}
      ariaLabel={`${registration.full_name} • ATL${registration.sequence}`}
      preview={(
        <ContainerWithOptions
          ariaLabel={t('more_registration_options')}
          renderOptions={() => (
            <>
              {hasEditPermission && registration.participant.active && (
                <Option
                  icon={<CheckCircle />}
                  label={t('set_status')}
                  onClick={() => openSetStatusModal()}
                />
              )}
              {hasEditPermission && (
                <Option
                  icon={<Mail />}
                  label={t('send_confirmation')}
                  onClick={() => openSendConfirmationModal()}
                />
              )}
              <ExternalOption
                icon={<Download />}
                label={t('download_ticket')}
                href={registration.ticket_url}
              />
              <LinkOption
                icon={<Eye />}
                label={t('view_order')}
                to={`/${project.id}/participants/${registration.purchase.order.participant.id}/orders/${registration.purchase.order.id}`}
              />
            </>
          )}
        >
          <UI.GridContainer sc={{ columns: ['1fr', '1fr fit-content(120px)'], gutter: 0.5 }}>
            <UI.Div sc={{ column: ['1 / span 2', '1'] }}>
              <UI.H4>
                <UI.Span
                  sc={{
                    muted: !registration.purchase.active || !registration.assigned,
                  }}
                >
                  {registration.assigned
                    ? registration.full_name
                    : (registration.invited_email ? t('invited') : t('not_assigned_yet'))}
                </UI.Span>
                {!registration.assigned && registration.invited_email && (
                  <>
                    {' '}
                    <UI.Span sc={{ fontWeight: 400, muted: true, mr: 1 }}>
                      {registration.invited_email}
                    </UI.Span>
                  </>
                )}
              </UI.H4>
              <UI.FlexContainer sc={{ wrap: 'wrap' }}>
                <TicketLabel
                  ticket={registration.ticket.title}
                  status={registration.status}
                  sc={{ mt: 0.5, mr: 1 }}
                />
                <RegistrationStatusLabel
                  status={registration.status}
                  sc={{ mt: 0.5, mr: 1 }}
                />
                {registration.purchase.promotion.title && (
                  <UI.Label sc={{ small: true, mt: 0.5, mr: 1 }}>
                    <UI.Icon>
                      <Tag />
                    </UI.Icon>
                    {' '}
                    {registration.purchase.promotion.title}
                  </UI.Label>
                )}
                {registration.time_slot && (
                  <UI.Label sc={{ small: true, mt: 0.5, mr: 1 }}>
                    <UI.Icon>
                      <Clock />
                    </UI.Icon>
                    {' '}
                    {formatTimeSlot(registration.time_slot)}
                  </UI.Label>
                )}
                {registration.ticket_number && (
                  <UI.Label sc={{ small: true, mt: 0.5 }}>
                    <UI.Icon>
                      <UI.Icons.StartFinish />
                    </UI.Icon>
                    {' '}
                    {registration.ticket_number}
                  </UI.Label>
                )}
              </UI.FlexContainer>
            </UI.Div>
            <UI.Div sc={{ small: true, textAlign: ['left', 'right'], mt: 0.25 }}>
              <UI.Div>
                <UI.Link
                  to={`?${encodeUrl({ event: registration.event.id, search: `ATL${registration.sequence}` })}`}
                  sc={{ secondary: true, fontWeight: 500 }}
                  onClick={() => handleAnchorClick()}
                >
                  ATL
                  {registration.sequence}
                </UI.Link>
              </UI.Div>
              <UI.Div sc={{ mt: 0.5, color: 'gray.500' }}>
                {formatDate(
                  parseDate(registration.created_at, { timezone: 'UTC' }),
                  { format: 'display_compact_date_time' },
                )}
              </UI.Div>
            </UI.Div>
          </UI.GridContainer>
        </ContainerWithOptions>
      )}
      body={(
        <UI.GridContainer>
          {registration.resale.public && (
            <UI.Info>
              <UI.H4>
                {t('ticket_listed_for_sale')}
              </UI.H4>
              {registration.resale.public_url && (
                <UI.A href={registration.resale.public_url} target="_blank" rel="noopener noreferer">
                  {t('view_listing')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              )}
              {registration.waitlist_invitees.filter((invite) => invite.pending_invite).length > 0 && (
                <UI.Span>
                  {t('waitlist_invitation_sent_warning')}
                </UI.Span>
              )}
            </UI.Info>
          )}

          {sections.map((section, index) => (
            <Fragment key={index}>
              {index > 0 && <UI.HR />}
              {...section}
            </Fragment>
          ))}

          {registration.upgrades.length > 0 && (
            <>
              <UI.HR />
              <UI.GridContainer sc={{ columns: ['1fr', '200px 1fr'], gutter: [0.25, 0.5] }}>
                <UI.Div>
                  <UI.Legend>
                    {t('extras')}
                  </UI.Legend>
                </UI.Div>
                <UI.Div>
                  <UI.GridContainer>
                    {registration.upgrades.map((upgrade) => (
                      <UpgradeDetails
                        upgrade={upgrade}
                        registration={registration}
                        key={upgrade.id}
                      />
                    ))}
                  </UI.GridContainer>
                </UI.Div>
              </UI.GridContainer>
            </>
          )}
        </UI.GridContainer>
      )}
    />
  );
};

export default RegistrationDetails;
